import classNames from 'classnames/bind';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { usePriceTransparency } from 'contexts/priceTransparency';
import { ConfirmTypeCode, IFareDetail } from 'list/international/types';
import { useContext, useMemo } from 'react';
import { PriceTransparency } from 'types/apollo';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

const getFreeFeePeriodStringFromDate = (freeDate: Date) => {
  const freeYear = freeDate.getFullYear().toString().slice(-2);
  const freeMonth = (freeDate.getMonth() + 1).toString().padStart(2, '0');
  const freeDay = freeDate.getDate().toString().padStart(2, '0');
  const hasMinutes = freeDate.getHours() > 0 && freeDate.getMinutes() > 0;
  const freeHour = freeDate.getHours().toString().padStart(2, '0');
  const freeMinute = freeDate.getMinutes().toString().padStart(2, '0');
  const daysOfWeek = ['일', '월', '화', '수', '목', '금', '토'];
  const dayOfWeek = daysOfWeek[freeDate.getDay()];
  return `${freeYear}. ${freeMonth}. ${freeDay}.(${dayOfWeek}) ${hasMinutes ? `${freeHour}:${freeMinute}` : '23:59'} 전까지 `;
};

interface IProps {
  fare: IFareDetail;
  isExpanded?: boolean;
  isFreeSameDayRefundable?: boolean;
  originalTotalFare: number;
  priceTransparency?: PriceTransparency;
  priceTransparencyLoading: boolean;
}

const Tags = ({
  fare,
  isExpanded,
  isFreeSameDayRefundable,
  originalTotalFare,
  priceTransparency,
  priceTransparencyLoading,
}: IProps) => {
  const setModal = useContext(ModalFuncContext);
  const { isHoneymoonBenefit, ConfirmType } = fare;
  const { setPriceTransparency, setOriginalTotalFare } = usePriceTransparency();
  const hasPriceTransparency = (priceTransparency?.detail?.length ?? 0) > 0;
  const priceTransparencyTag = useMemo(
    () => priceTransparency?.tag,
    [priceTransparency],
  );
  const onDetailClick = () => {
    setPriceTransparency(priceTransparency?.detail ?? []);
    setOriginalTotalFare(originalTotalFare);
    setModal({ type: Modal.POLICY, position: ModalPosition.DETAIL });
  };

  if (priceTransparencyLoading) {
    return (
      <div className={cx('Tags')} aria-busy="true">
        &#32;
      </div>
    );
  }

  return (
    <div className={cx('Tags')}>
      {hasPriceTransparency && (
        <>
          {priceTransparencyTag?.cancel?.identified ? (
            priceTransparencyTag.cancel.available ? (
              <span className={cx('tag', 'type_info', 'as_price')}>
                환불가능 (
                {priceTransparencyTag.cancel.isFeeNeeded ||
                !priceTransparencyTag.cancel.cancelFeePeriod ||
                new Date(priceTransparencyTag.cancel.cancelFeePeriod) <
                  new Date()
                  ? '수수료 지불시'
                  : `${getFreeFeePeriodStringFromDate(new Date(priceTransparencyTag.cancel.cancelFeePeriod))}항공사 수수료 무료`}
                )
              </span>
            ) : (
              <span className={cx('tag', 'type_warning', 'as_prohibition')}>
                환불불가
              </span>
            )
          ) : (
            <span className={cx('tag', 'type_warning', 'as_needcheck')}>
              환불정보 확인필요
            </span>
          )}
        </>
      )}
      {isFreeSameDayRefundable && (
        <span className={cx('tag', 'type_freeSameDayRefundable')}>
          결제당일 무료취소
        </span>
      )}
      {hasPriceTransparency && (
        <>
          {isExpanded && (
            <>
              {priceTransparencyTag?.change?.identified ? (
                priceTransparencyTag.change.available ? (
                  <span className={cx('tag', 'type_info', 'as_price')}>
                    일정변경가능 (
                    {priceTransparencyTag.change.isFeeNeeded ||
                    !priceTransparencyTag.change.changeFeePeriod ||
                    new Date(priceTransparencyTag.change.changeFeePeriod) <
                      new Date()
                      ? '수수료 지불시'
                      : `${getFreeFeePeriodStringFromDate(new Date(priceTransparencyTag.change.changeFeePeriod))}항공사 수수료 무료`}
                    )
                  </span>
                ) : (
                  <span className={cx('tag', 'type_warning', 'as_prohibition')}>
                    일정변경불가
                  </span>
                )
              ) : (
                <span className={cx('tag', 'type_warning', 'as_needcheck')}>
                  일정변경 확인필요
                </span>
              )}
            </>
          )}
          {priceTransparencyTag?.cabin?.identified ? (
            priceTransparencyTag.cabin.available &&
            (priceTransparencyTag.cabin.quantity ?? 0) > 0 ? (
              isExpanded && (
                <span className={cx('tag', 'type_info', 'as_luggage2')}>
                  기내수하물{' '}
                  {priceTransparencyTag.cabin.quantity
                    ? `${priceTransparencyTag.cabin.quantity}pc`
                    : ''}
                  {priceTransparencyTag.cabin.quantity &&
                  priceTransparencyTag.cabin.weight
                    ? ', '
                    : ' '}
                  {priceTransparencyTag.cabin.weight
                    ? `${priceTransparencyTag.cabin.weight}kg `
                    : ' '}
                  포함
                </span>
              )
            ) : (
              <span className={cx('tag', 'type_warning', 'as_prohibition')}>
                기내수하물 미포함
              </span>
            )
          ) : (
            <span className={cx('tag', 'type_warning', 'as_needcheck')}>
              기내수하물정보 확인필요
            </span>
          )}
          {priceTransparencyTag?.checked?.identified ? (
            priceTransparencyTag.checked.available &&
            (priceTransparencyTag.checked.quantity ?? 0) > 0 ? (
              isExpanded && (
                <span className={cx('tag', 'type_info', 'as_luggage')}>
                  위탁수하물{' '}
                  {priceTransparencyTag.checked.quantity
                    ? `${priceTransparencyTag.checked.quantity}pc`
                    : ''}
                  {priceTransparencyTag.checked.quantity &&
                  priceTransparencyTag.checked.weight
                    ? ', '
                    : ' '}
                  {priceTransparencyTag.checked.weight
                    ? `${priceTransparencyTag.checked.weight}kg `
                    : ' '}
                  포함
                </span>
              )
            ) : (
              <span className={cx('tag', 'type_warning', 'as_prohibition')}>
                위탁수하물 미포함
              </span>
            )
          ) : (
            <span className={cx('tag', 'type_warning', 'as_needcheck')}>
              위탁수하물정보 확인필요
            </span>
          )}
        </>
      )}
      {isHoneymoonBenefit && (
        <span className={cx('tag', 'type_honeymoon')}>허니문혜택</span>
      )}
      {hasPriceTransparency && (
        <>
          {priceTransparencyTag?.isMileageAvailable && (
            <span className={cx('tag', 'type_info', 'as_mileage')}>
              마일리지 적립가능
            </span>
          )}
          {priceTransparencyTag?.isSeatSelectionAvailable && isExpanded && (
            <span className={cx('tag', 'type_info', 'as_seat')}>
              사전좌석지정 무료
            </span>
          )}
        </>
      )}
      {ConfirmType !== ConfirmTypeCode.CONFIRMED && (
        <span className={cx('tag', 'type_warning', 'as_notconfirmed')}>
          미확정요금
        </span>
      )}
      {hasPriceTransparency && (
        <button
          type="button"
          className={cx('view_detail')}
          data-event-nlog="click"
          data-event-area-code="idt.ptdetail"
          onClick={onDetailClick}
        >
          상세규정
        </button>
      )}
    </div>
  );
};

export default Tags;
